import { useEffect, useRef } from 'react';
import { startMeasure, endMeasure } from '../utils/performanceMonitor';

/**
 * Hook to monitor component performance
 * @param {string} componentName - Name of the component to monitor
 * @param {Array} dependencies - Optional dependencies array to track re-renders
 * @returns {Object} - Performance monitoring methods
 */
const usePerformanceMonitoring = (componentName, dependencies = []) => {
  const renderCount = useRef(0);
  const lastRenderTime = useRef(null);
  
  // Track initial render
  useEffect(() => {
    const name = `${componentName}_initial_render`;
    startMeasure(name);
    
    return () => {
      endMeasure(name);
    };
  }, []); // Empty dependency array for initial render only
  
  // Track re-renders based on dependencies
  useEffect(() => {
    renderCount.current += 1;
    
    if (renderCount.current > 1) {
      const name = `${componentName}_rerender_${renderCount.current}`;
      startMeasure(name);
      lastRenderTime.current = Date.now();
      
      return () => {
        endMeasure(name);
      };
    }
  }, dependencies);
  
  // Track component unmount
  useEffect(() => {
    return () => {
      console.log(`Component ${componentName} unmounted after ${renderCount.current} renders`);
    };
  }, [componentName]);
  
  return {
    renderCount: renderCount.current,
    trackOperation: (operationName) => {
      const name = `${componentName}_${operationName}`;
      startMeasure(name);
      return () => endMeasure(name);
    },
    logPerformance: (message) => {
      console.log(`[${componentName} Performance] ${message}`);
    }
  };
};

export default usePerformanceMonitoring;
